import Button from 'tio-reimagined/components/button';
import { dropTask } from 'ember-concurrency';
import { Input, Switch } from 'tio-ui/components/forms';
import { on } from '@ember/modifier';
import { PhLock } from 'ember-phosphor-icons';
import { Section } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import formatPhone from 'tio-common/helpers/format-phone';
import inputmask from 'tio-common/modifiers/inputmask';
import type EmployeeModel from 'tio-common/models/employee';
import type PersonModel from 'tio-common/models/person';
import type Owner from '@ember/owner';
import type PhoneNumber from 'tio-common/models/phone-number';
import type SessionContextService from '../../../../services/session-context';
import type SessionService from '../../../../services/session';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';

export interface SecuritySettingsSignature {
  Args: {
    employee?: EmployeeModel;
    familyPerson?: PersonModel;
  };
}

export default class SecuritySettings extends Component<SecuritySettingsSignature> {
  @service declare session: SessionService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;

  @tracked isOpen = true;
  @tracked isLoading = false;
  @tracked error = null;
  @tracked isEditingContact = false;
  @tracked phoneValue!: string;

  constructor(owner: Owner, args: SecuritySettingsSignature['Args']) {
    super(owner, args);
    this.setInitialPhoneValue();
  }

  get person() {
    return this.args.familyPerson ?? this.args.employee?.person;
  }

  get primaryPhoneNumber() {
    return this.args.employee?.primaryPhoneNumber ?? this.person?.primaryPhoneNumber;
  }

  setInitialPhoneValue() {
    const raw = this.primaryPhoneNumber?.number || '';
    const digits = raw.replace(/\D/g, '');
    // Normalize to +1XXXXXXXXXX so the mask can display it
    if (digits.length === 10) {
      this.phoneValue = `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      this.phoneValue = `+${digits}`;
    } else {
      this.phoneValue = raw;
    }
  }

  get login() {
    return this.person?.user?.logins?.find((login) => login.isActive);
  }

  get userName() {
    return this.login ? this.login.userName : '';
  }

  get isMfaEnabled() {
    return this.login?.twoFactorEnabled ?? false;
  }

  get mfaPhoneNumber() {
    return this.primaryPhoneNumber?.number || this.phoneValue || '';
  }

  get isMfaToggleDisabled() {
    return this.updateMfaSettings.isRunning || (!this.isPhoneNumberValid && !this.isMfaEnabled);
  }

  get showMfaPhonePrompt() {
    return !this.isPhoneNumberValid && !this.isMfaEnabled;
  }

  get showMfaPhoneNumber() {
    return !this.showMfaPhonePrompt && !!this.mfaPhoneNumber;
  }

  toggleExpand = () => {
    this.isOpen = !this.isOpen;
  };

  toggleContactEdit = () => {
    this.isEditingContact = !this.isEditingContact;
    this.setInitialPhoneValue();
  };

  setPhoneValue = (value: string) => {
    // Store raw value during editing; normalization happens on blur
    this.phoneValue = value || '';
  };

  onPhoneBlur = () => {
    this.normalizePhoneValue();
  };

  private normalizePhoneValue() {
    // Normalize phone to +1XXXXXXXXXX format
    const digits = this.phoneValue.replace(/\D/g, '');
    if (digits.length === 10) {
      this.phoneValue = `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      this.phoneValue = `+${digits}`;
    }
  }

  get isPhoneNumberValid() {
    const digits = this.phoneValue.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  }

  get isSubmitDisabled() {
    return !this.isPhoneNumberValid || this.phoneValue === this.primaryPhoneNumber?.number;
  }

  savePhoneNumber = dropTask(async () => {
    // Ensure phone is normalized before saving
    this.normalizePhoneValue();
    const normalizedPhone = this.phoneValue;

    if (this.primaryPhoneNumber) {
      this.primaryPhoneNumber.number = normalizedPhone;
      await this.primaryPhoneNumber.save({
        adapterOptions: { changelog: 'updated by employee' },
      });
    } else if (this.person?.user) {
      const newPhoneNumber = this.store.createRecord<PhoneNumber>('phone-number', {
        contactType: 'TELEPHONE',
        number: normalizedPhone,
        phoneNumberType: 'MOBILE',
        user: this.person.user,
      });
      await newPhoneNumber.save({ adapterOptions: { changelog: 'added by employee' } });
    }

    if (this.args.employee) {
      await this.args.employee.reload({ include: 'phone-numbers' });
    } else if (this.args.familyPerson) {
      await this.args.familyPerson.reload({ include: 'phone-numbers' });
    }
    this.toggleContactEdit();
  });

  updateMfaSettings = dropTask(async (isSelected: boolean) => {
    if (!this.login) {
      return;
    }

    if (isSelected && !this.isPhoneNumberValid) {
      return;
    }

    this.login.twoFactorEnabled = isSelected;
    await this.login.save();
  });

  <template>
    <Section>
      <:header>{{t "profile.security_settings"}}</:header>
      <:body>
        <div class="flex flex-col">
          <div class="flex flex-row mx-8 my-4">
            <div class="self-center w-1/4 font-semibold text-gray-700">
              {{t "profile.personal_info"}}
            </div>
            <div class="flex flex-col w-1/5">
              <span class="text-xs">{{t "login.first_name"}}</span>
              <span class="ml-4 text-xl my-4">{{this.person.firstName}}</span>
            </div>
            <div class="flex flex-col w-1/5">
              <span class="text-xs">{{t "login.last_name"}}</span>
              <span class="ml-4 text-xl my-4">{{this.person.lastName}}</span>
            </div>
            <div class="flex-col ml-auto w-1/6 text-gray-500 grid justify-items-center">
              <PhLock @size="24" @weight="fill" />
              <span class="text-xs">{{t "profile.to_edit_contact_support"}}</span>
            </div>
          </div>
          <hr />
          <div class="flex flex-row mx-8 my-4">
            <div class="self-center w-1/4 font-semibold text-gray-700">
              {{t "profile.username_login"}}
            </div>
            <div class="flex flex-col w-1/5">
              <span class="text-xs">{{t "profile.username"}}</span>
              <span class="text-xl my-4 ml-4">{{this.userName}}</span>
            </div>
            <div class="flex-col ml-auto w-1/6 text-gray-500 grid justify-items-center">
              <PhLock @size="24" @weight="fill" />
              <span class="text-xs">{{t "profile.to_edit_contact_support"}}</span>
            </div>
          </div>
          <hr />
          <div class="flex flex-row mx-8 my-4">
            <div class="self-center w-1/4 font-semibold text-gray-700">
              {{t "profile.contact_info"}}
            </div>
            <div class="flex flex-col">
              {{#if this.isEditingContact}}
                <Input
                  @value={{this.phoneValue}}
                  @type="tel"
                  @onChange={{this.setPhoneValue}}
                  @onBlur={{this.onPhoneBlur}}
                  @label={{t "profile.mobile_phone"}}
                  placeholder="+1 (###) ###-####"
                  {{inputmask mask="+1 (999) 999-9999"}}
                  data-test-phone-input
                />
              {{else if this.phoneValue}}
                <span class="text-xl my-4 ml-4">
                  {{formatPhone this.phoneValue}}
                </span>
              {{else}}
                <span class="text-xs my-4 ml-4 text-red-500">
                  {{t "profile.no_phone"}}
                </span>
              {{/if}}
            </div>
            <div class="flex flex-row justify-end self-center ml-auto w-1/6 gap-x-2">
              {{#if this.isEditingContact}}
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  {{on "click" this.savePhoneNumber.perform}}
                  @isDisabled={{this.isSubmitDisabled}}
                  data-test-contact-save
                >
                  {{t "common.save"}}
                </Button>
                <Button
                  @appearance="outlined"
                  @intent="danger"
                  {{on "click" this.toggleContactEdit}}
                  data-test-contact-cancel
                >
                  {{t "common.cancel"}}
                </Button>
              {{else}}
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  {{on "click" this.toggleContactEdit}}
                  class="self-auto"
                  data-test-contact-edit
                >
                  {{t "common.edit"}}
                </Button>
              {{/if}}
            </div>
          </div>
          <hr />
          <div class="flex flex-row mx-8 my-4" data-test-mfa-section>
            <div class="self-center w-1/4 font-semibold text-gray-700">
              {{t "profile.mfa.title"}}
            </div>
            <div class="flex flex-col w-2/5">
              <span class="text-xs">{{t "profile.mfa.status"}}</span>
              <span class="text-xl my-4 ml-4" data-test-mfa-status>
                {{if
                  this.isMfaEnabled
                  (t "profile.mfa.status_enabled")
                  (t "profile.mfa.status_disabled")
                }}
              </span>
              {{#if this.showMfaPhonePrompt}}
                <span class="text-xs text-red-500 ml-4" data-test-mfa-phone-required>
                  {{t "profile.mfa.phone_required"}}
                </span>
              {{else if this.showMfaPhoneNumber}}
                <span class="text-xs text-gray-500 ml-4" data-test-mfa-phone-number>
                  {{t "profile.mfa.sms_number"}}
                  {{formatPhone this.mfaPhoneNumber}}
                </span>
              {{/if}}
            </div>
            <div class="flex flex-row justify-end self-center ml-auto w-1/6 gap-x-2">
              <div data-test-mfa-toggle>
                <Switch
                  @isSelected={{this.isMfaEnabled}}
                  @onChange={{this.updateMfaSettings.perform}}
                  @isDisabled={{this.isMfaToggleDisabled}}
                  @label={{t "profile.mfa.toggle_label"}}
                />
              </div>
            </div>
          </div>
        </div>
      </:body>
    </Section>
  </template>
}
